/**
 * V2-702.5 (Phase 3): Operator action modals for the Analytics detail pane.
 *
 *   - RefetchOutcomeModal: fetches a fresh polymarket resolution and
 *     surfaces a current-vs-proposed diff; on confirm calls
 *     `apply-outcome` to persist the proposed values (audit-logged).
 *   - EditOrderModal:      free-form edit of an order's resolution
 *     fields (status, pnl, size, price, fees, BTC open/close); each
 *     changed field hits PATCH /v2/orders/:id (audit-logged per field).
 *   - HideWindowConfirm:   confirm-then-toggle the window's `hidden` flag.
 *
 * On success each modal:
 *   1. shows a Mantine notification,
 *   2. invalidates `["analytics"]` and `["analytics", "window", slug]`
 *      so the list KPIs and the detail pane both re-render,
 *   3. closes itself.
 *
 * All write paths route through the typed fetch client in
 * `../api/client.ts`, so failures bubble up as `ApiClientError` and the
 * UI surfaces the backend's error envelope rather than a stack trace.
 */
import { useEffect, useMemo, useState } from "react";
import {
  Alert, Badge, Button, Code, Divider, Group, Modal, Select, Stack, Switch,
  Table, Text, TextInput,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api, ApiClientError } from "../api/client";
import type {
  AnalyticsBet, AnalyticsWindowDetail, OutcomeProposal,
} from "../api/schemas";
import { LoadingState } from "../components/ui";
import { monoStyle } from "../theme";

// ============================================================ Re-fetch outcome

interface RefetchOutcomeProps {
  slug: string;
  opened: boolean;
  onClose: () => void;
}

export function RefetchOutcomeModal({ slug, opened, onClose }: RefetchOutcomeProps) {
  const qc = useQueryClient();

  // Lazy: only hit polymarket when the modal is actually open.
  const proposalQ = useQuery({
    queryKey: ["analytics", "window", slug, "refetch-outcome"],
    queryFn: () => api.analytics.refetchOutcome(slug),
    enabled: opened,
    staleTime: 0,
    gcTime: 0,
    retry: false,
  });

  const apply = useMutation({
    mutationFn: async (proposal: OutcomeProposal) => {
      if (!proposal.proposed.winning_direction) {
        throw new Error("Polymarket has not resolved this market yet — nothing to apply.");
      }
      const po = proposal.proposed_order ?? null;
      const ee = proposal.early_exit ?? null;
      return api.analytics.applyOutcome(slug, {
        winning_direction: proposal.proposed.winning_direction,
        outcome_source: proposal.proposed.outcome_source,
        net_pnl_usdc: proposal.proposed.net_pnl_usdc ?? null,
        btc_start: proposal.proposed.btc_start ?? null,
        btc_end: proposal.proposed.btc_end ?? null,
        btc_delta: proposal.proposed.btc_delta ?? null,
        ...(po ? {
          create_order: {
            direction: po.direction,
            filled_shares: po.filled_shares,
            size_usdc: po.size_usdc,
            price: po.avg_price ?? null,
            fee_usdc: po.fee_usdc ?? null,
            pnl_usdc: po.pnl_usdc ?? null,
            mode: po.mode,
          },
        } : {}),
        ...(ee ? {
          record_sell: {
            exit_at: ee.exit_at,
            exit_reason: ee.exit_reason,
            sell_shares: ee.sell_shares ?? null,
            sell_price: ee.sell_price ?? null,
            sell_usdc: ee.sell_usdc ?? null,
            pnl_usdc: ee.pnl_usdc ?? null,
          },
        } : {}),
      });
    },
    onSuccess: () => {
      notifications.show({ color: "green", title: "Outcome applied", message: slug });
      qc.invalidateQueries({ queryKey: ["analytics"] });
      onClose();
    },
    onError: (e: unknown) => notifyError("Apply outcome failed", e),
  });

  return (
    <Modal opened={opened} onClose={onClose} title="Re-fetch outcome from Polymarket" size="lg">
      {proposalQ.isLoading ? <LoadingState label="Fetching from polymarket…" /> :
       proposalQ.isError ? (
         <Alert color="red" title="Polymarket fetch failed">
           {(proposalQ.error as Error)?.message ?? "Unknown error"}
         </Alert>
       ) : proposalQ.data ? (
         <Stack gap="md">
           <Text size="sm" c="dimmed">
             Window <Code style={monoStyle}>{slug}</Code>
             {proposalQ.data.window_only
               ? " · no order on this window — winning direction will be recorded but no PnL touched."
               : proposalQ.data.proposed_order
               ? ` · found ${proposalQ.data.proposed_order.trade_count} Polymarket buy trade(s) for your funder address — applying will reconcile this window's order details from Polymarket activity.`
               : null}
           </Text>
           {proposalQ.data.proposed_order?.has_realized_exits ? (
             <Alert color="yellow" title="Partial early-exit detected">
               Polymarket also has SELL or REDEEM entries for this market on
               your account. The reconciled order assumes the position was
               held to resolution. If you sold early, edit the order's
               `pnl_usdc` and tick `sold_early` after applying.
             </Alert>
           ) : null}
           <DiffTable proposal={proposalQ.data} />
           {!proposalQ.data.changed ? (
             <Alert color="gray">Already in sync — nothing to apply.</Alert>
           ) : null}
           <Group justify="flex-end" gap="xs">
             <Button variant="default" onClick={onClose}>Cancel</Button>
             <Button
               color="cyan"
               onClick={() => apply.mutate(proposalQ.data!)}
               disabled={!proposalQ.data.changed || !proposalQ.data.proposed.winning_direction}
               loading={apply.isPending}
             >
               Apply proposed
             </Button>
           </Group>
         </Stack>
       ) : null}
    </Modal>
  );
}

function DiffTable({ proposal }: { proposal: OutcomeProposal }) {
  const cur = proposal.current;
  const pr  = proposal.proposed;
  return (
    <Table withTableBorder horizontalSpacing="sm" verticalSpacing={6} fz="sm" style={monoStyle}>
      <Table.Thead>
        <Table.Tr>
          <Table.Th>Field</Table.Th>
          <Table.Th>Current</Table.Th>
          <Table.Th>Proposed</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        <DiffRow
          label="Winning direction"
          a={cur.winning_direction ?? "—"}
          b={pr.winning_direction ?? "—"}
          changed={cur.winning_direction !== pr.winning_direction}
        />
        <DiffRow
          label="Won?"
          a={fmtBool(cur.won)} b={fmtBool(pr.won)}
          changed={cur.won !== pr.won}
        />
        <DiffRow
          label="Net P/L"
          a={cur.net_pnl_usdc ?? "—"} b={pr.net_pnl_usdc ?? "—"}
          changed={cur.net_pnl_usdc !== pr.net_pnl_usdc}
        />
        <DiffRow
          label="Outcome source"
          a={cur.outcome_source ?? "—"} b={pr.outcome_source}
          changed={(cur.outcome_source ?? null) !== pr.outcome_source}
        />
        <DiffRow
          label="BTC start"
          a={cur.btc_start ?? "—"}
          b={pr.btc_start ?? "—"}
          changed={(cur.btc_start ?? null) !== (pr.btc_start ?? null)}
        />
        <DiffRow
          label="BTC end"
          a={cur.btc_end ?? "—"}
          b={pr.btc_end ?? "—"}
          changed={(cur.btc_end ?? null) !== (pr.btc_end ?? null)}
        />
        <DiffRow
          label="BTC delta"
          a={cur.btc_delta ?? "—"}
          b={pr.btc_delta ?? "—"}
          changed={(cur.btc_delta ?? null) !== (pr.btc_delta ?? null)}
        />
        {proposal.proposed_order ? (
          <>
            <DiffRow
              label="Direction (reconciled)"
              a="—"
              b={proposal.proposed_order.direction}
              changed
            />
            <DiffRow
              label="Filled shares (reconciled)"
              a="—"
              b={proposal.proposed_order.filled_shares}
              changed
            />
            <DiffRow
              label="Size USDC (reconciled)"
              a="—"
              b={proposal.proposed_order.size_usdc}
              changed
            />
            <DiffRow
              label="Avg fill price (reconciled)"
              a="—"
              b={proposal.proposed_order.avg_price}
              changed
            />
          </>
        ) : null}
      </Table.Tbody>
    </Table>
  );
}

function DiffRow({ label, a, b, changed }: { label: string; a: string; b: string; changed: boolean }) {
  return (
    <Table.Tr style={changed ? { background: "var(--mantine-color-cyan-9)" } : undefined}>
      <Table.Td>{label}</Table.Td>
      <Table.Td c="dimmed">{a}</Table.Td>
      <Table.Td>
        {b}
        {changed ? <Badge ml={6} size="xs" color="cyan">change</Badge> : null}
      </Table.Td>
    </Table.Tr>
  );
}

function fmtBool(b: boolean | null): string {
  if (b == null) return "—";
  return b ? "yes" : "no";
}

// ===================================================================== Edit

interface EditOrderProps {
  detail: AnalyticsWindowDetail;
  opened: boolean;
  onClose: () => void;
}

interface EditFormState {
  winning_direction: "UP" | "DOWN" | "" | null; // "" maps to null on submit
  outcome_source: string;
  status: "won" | "lost" | "no_fill" | "";
  pnl_usdc: string;
  size_usdc: string;
  price: string;
  filled_shares: string;
  fee_usdc: string;
  btc_open: string;
  btc_close: string;
  sold_early: boolean;
  // "" maps to null on submit (i.e. "clear the exit reason")
  exit_reason: "take_profit" | "stop_loss" | "manual" | "momentum_sell" | "";
}

function initialFormFromDetail(d: AnalyticsWindowDetail, bet: AnalyticsBet): EditFormState {
  // Map current bet_status → operator-status enum.
  const status: EditFormState["status"] =
    bet.bet_status === "won" ? "won"
    : bet.bet_status === "lost" ? "lost"
    : bet.bet_status === "no_fill" ? "no_fill"
    : "";
  return {
    winning_direction: d.winning_direction ?? "",
    outcome_source: d.outcome_source ?? "",
    status,
    pnl_usdc: bet.pnl_usdc ?? "",
    size_usdc: bet.size_usdc ?? "",
    price: bet.price ?? "",
    filled_shares: bet.filled_shares ?? bet.size_shares ?? "",
    fee_usdc: bet.fee_usdc ?? "",
    btc_open: d.btc_open ?? "",
    btc_close: d.btc_close ?? "",
    sold_early: bet.sold_early,
    exit_reason: bet.exit_reason ?? "",
  };
}

export function EditOrderModal({ detail, opened, onClose }: EditOrderProps) {
  const qc = useQueryClient();
  const bet = detail.bet;
  const initial = useMemo<EditFormState>(() => {
    if (bet) return initialFormFromDetail(detail, bet);
    return {
      winning_direction: (detail.winning_direction ?? "") as EditFormState["winning_direction"],
      outcome_source: detail.outcome_source ?? "",
      status: "" as EditFormState["status"],
      pnl_usdc: "",
      size_usdc: "",
      price: "",
      filled_shares: "",
      fee_usdc: "",
      btc_open: detail.btc_open ?? "",
      btc_close: detail.btc_close ?? "",
      sold_early: false,
      exit_reason: "" as EditFormState["exit_reason"],
    };
  }, [detail, bet]);
  const [form, setForm] = useState<EditFormState | null>(initial);

  // Reset the form whenever the modal re-opens or the underlying detail
  // changes (e.g. user picked a different window then re-opened Edit).
  useEffect(() => {
    if (opened) setForm(initial);
  }, [opened, initial]);

  const save = useMutation({
    mutationFn: async () => {
      if (!form || !initial) throw new Error("nothing to save");
      if (!bet) {
        const nextWinner = form.winning_direction === "" ? null : form.winning_direction;
        if (nextWinner == null) {
          throw new Error("Winning direction is required when editing a window without an order.");
        }
        const nextOutcome = form.outcome_source === "" ? null : form.outcome_source;
        const nextBtcStart = form.btc_open === "" ? null : form.btc_open;
        const nextBtcEnd = form.btc_close === "" ? null : form.btc_close;
        const nextBtcDelta = nextBtcStart != null && nextBtcEnd != null
          ? (Number(nextBtcEnd) - Number(nextBtcStart)).toString()
          : null;

        const changed =
          (detail.winning_direction ?? null) !== nextWinner
          || (detail.outcome_source ?? null) !== nextOutcome
          || (detail.btc_open ?? null) !== nextBtcStart
          || (detail.btc_close ?? null) !== nextBtcEnd
          || (detail.btc_delta ?? null) !== nextBtcDelta;
        if (!changed) throw new Error("No changes to save.");

        return api.analytics.applyOutcome(detail.slug, {
          winning_direction: nextWinner,
          outcome_source: nextOutcome ?? "manual",
          btc_start: nextBtcStart,
          btc_end: nextBtcEnd,
          btc_delta: nextBtcDelta,
          net_pnl_usdc: null,
        });
      }
      // Build a delta — only ship what the operator actually touched. The
      // backend treats `undefined` as "no change" and `null` as "clear",
      // matching V1's edit_order semantics.
      const body: Parameters<typeof api.ordersWrite.edit>[1] = {};
      if (form.winning_direction !== initial.winning_direction)
        body.winning_direction = form.winning_direction === "" ? null : form.winning_direction;
      if (form.outcome_source !== initial.outcome_source)
        body.outcome_source = form.outcome_source === "" ? null : form.outcome_source;
      if (form.status !== initial.status && form.status !== "")
        body.status = form.status;
      if (form.pnl_usdc      !== initial.pnl_usdc)      body.pnl_usdc      = form.pnl_usdc      === "" ? null : form.pnl_usdc;
      if (form.size_usdc     !== initial.size_usdc)     body.size_usdc     = form.size_usdc     === "" ? null : form.size_usdc;
      if (form.price         !== initial.price)         body.price         = form.price         === "" ? null : form.price;
      if (form.filled_shares !== initial.filled_shares) body.filled_shares = form.filled_shares === "" ? null : form.filled_shares;
      if (form.fee_usdc      !== initial.fee_usdc)      body.fee_usdc      = form.fee_usdc      === "" ? null : form.fee_usdc;
      if (form.btc_open      !== initial.btc_open)      body.btc_open      = form.btc_open      === "" ? null : form.btc_open;
      if (form.btc_close     !== initial.btc_close)     body.btc_close     = form.btc_close     === "" ? null : form.btc_close;
      if (form.sold_early    !== initial.sold_early)    body.sold_early    = form.sold_early;
      if (form.exit_reason   !== initial.exit_reason)   body.exit_reason   = form.exit_reason   === "" ? null : form.exit_reason;

      if (Object.keys(body).length === 0) {
        throw new Error("No changes to save.");
      }
      return api.ordersWrite.edit(bet.order_id, body);
    },
    onSuccess: () => {
      notifications.show({
        color: "green",
        title: bet ? "Order updated" : "Window updated",
        message: bet?.order_id ?? detail.slug,
      });
      qc.invalidateQueries({ queryKey: ["analytics"] });
      onClose();
    },
    onError: (e: unknown) => notifyError("Edit failed", e),
  });

  return (
    <Modal opened={opened} onClose={onClose} title={bet ? `Edit order · ${bet.order_id}` : `Edit window · ${detail.slug}`} size="lg">
      {form ? (
        <Stack gap="sm">
          <Group grow>
            <Select
              label="Winning direction (window)"
              data={[
                { value: "", label: "— (clear)" },
                { value: "UP",   label: "UP" },
                { value: "DOWN", label: "DOWN" },
              ]}
              value={form.winning_direction ?? ""}
              onChange={(v) => setForm((f) => f && ({ ...f, winning_direction: (v as "UP" | "DOWN" | "") ?? "" }))}
              comboboxProps={{ withinPortal: false }}
            />
            <Select
              label="Order status"
              data={[
                { value: "",        label: "— (no change)" },
                { value: "won",     label: "won" },
                { value: "lost",    label: "lost" },
                { value: "no_fill", label: "no_fill" },
              ]}
              value={form.status}
              onChange={(v) => setForm((f) => f && ({ ...f, status: (v as EditFormState["status"]) ?? "" }))}
              comboboxProps={{ withinPortal: false }}
              disabled={!bet}
            />
          </Group>
          <TextInput
            label="Outcome source"
            value={form.outcome_source}
            onChange={(e) => setForm((f) => f && ({ ...f, outcome_source: e.target.value }))}
            placeholder="e.g. polymarket / chainlink / manual"
          />
          <Divider label="Money" labelPosition="left" />
          <Group grow>
            <TextInput label="Net P/L (USDC)"  value={form.pnl_usdc}      onChange={(e) => setForm((f) => f && ({ ...f, pnl_usdc:  e.target.value }))} disabled={!bet} />
            <TextInput label="Size (USDC)"     value={form.size_usdc}     onChange={(e) => setForm((f) => f && ({ ...f, size_usdc: e.target.value }))} disabled={!bet} />
            <TextInput label="Fee (USDC)"      value={form.fee_usdc}      onChange={(e) => setForm((f) => f && ({ ...f, fee_usdc:  e.target.value }))} disabled={!bet} />
          </Group>
          <Group grow>
            <TextInput label="Price"           value={form.price}         onChange={(e) => setForm((f) => f && ({ ...f, price:    e.target.value }))} disabled={!bet} />
            <TextInput label="Filled shares"   value={form.filled_shares} onChange={(e) => setForm((f) => f && ({ ...f, filled_shares: e.target.value }))} disabled={!bet} />
          </Group>
          <Divider label="BTC" labelPosition="left" />
          <Group grow>
            <TextInput label="BTC open"  value={form.btc_open}  onChange={(e) => setForm((f) => f && ({ ...f, btc_open:  e.target.value }))} />
            <TextInput label="BTC close" value={form.btc_close} onChange={(e) => setForm((f) => f && ({ ...f, btc_close: e.target.value }))} />
          </Group>

          <Divider label="Exit" labelPosition="left" />
          <Group grow align="flex-end">
            <Switch
              label="Sold early"
              description="Position was closed before window resolution"
              checked={form.sold_early}
              onChange={(e) => setForm((f) => f && ({ ...f, sold_early: e.currentTarget.checked }))}
              disabled={!bet}
            />
            <Select
              label="Exit reason"
              data={[
                { value: "",            label: "— (clear)" },
                { value: "take_profit", label: "Take-profit" },
                { value: "stop_loss",   label: "Stop-loss" },
                { value: "manual",      label: "Manual sell" },
                { value: "momentum_sell", label: "Momentum sell" },
              ]}
              value={form.exit_reason}
              onChange={(v) => setForm((f) => f && ({ ...f, exit_reason: (v as EditFormState["exit_reason"]) ?? "" }))}
              comboboxProps={{ withinPortal: false }}
              disabled={!bet}
            />
          </Group>

          <Group justify="flex-end" gap="xs" mt="sm">
            <Button variant="default" onClick={onClose}>Cancel</Button>
            <Button color="cyan" onClick={() => save.mutate()} loading={save.isPending}>
              Save
            </Button>
          </Group>
        </Stack>
      ) : null}
    </Modal>
  );
}

// ============================================================== Hide / Unhide

interface HideWindowProps {
  slug: string;
  hidden: boolean;
  opened: boolean;
  onClose: () => void;
}

export function HideWindowConfirm({ slug, hidden, opened, onClose }: HideWindowProps) {
  const qc = useQueryClient();
  const next = !hidden;
  const action = next ? "Hide" : "Unhide";

  const mut = useMutation({
    mutationFn: () => api.analytics.setVisibility(slug, next),
    onSuccess: () => {
      notifications.show({
        color: next ? "gray" : "green",
        title: `Window ${next ? "hidden" : "unhidden"}`,
        message: slug,
      });
      qc.invalidateQueries({ queryKey: ["analytics"] });
      onClose();
    },
    onError: (e: unknown) => notifyError(`${action} failed`, e),
  });

  return (
    <Modal opened={opened} onClose={onClose} title={`${action} window?`} size="sm">
      <Stack gap="sm">
        <Text size="sm">
          {next
            ? "Hiding this window removes it from default analytics and simulation inputs. You can unhide it later by enabling “Show hidden”."
            : "This will make the window visible again in analytics and simulation inputs."}
        </Text>
        <Code block style={monoStyle}>{slug}</Code>
        <Group justify="flex-end" gap="xs">
          <Button variant="default" onClick={onClose}>Cancel</Button>
          <Button color={next ? "red" : "green"} onClick={() => mut.mutate()} loading={mut.isPending}>
            {action}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

// ============================================================ shared helpers

function notifyError(title: string, e: unknown) {
  const msg = e instanceof ApiClientError
    ? `${e.code}: ${e.message}`
    : e instanceof Error ? e.message : String(e);
  notifications.show({ color: "red", title, message: msg });
}
